'use client';
import React from 'react';
import type { LearnResponsePage } from '@benzinga/content-manager';
import { Icon } from '@benzinga/core-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { LearnSectionLabel } from './LearnSectionLabel';

const AUTHOR_URL_BASE = 'https://www.benzinga.com/money/author/';

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

interface AuthorBioBlockData {
  bio?: string;
  image?: string;
  type?: string;
  url?: string;
  user_name?: string;
}

const findAuthorBioData = (post: LearnResponsePage): AuthorBioBlockData | null => {
  const blocks = Array.isArray(post.content_footer?.blocks) ? post.content_footer.blocks : [];
  const authorBlocks = blocks.filter(block => block?.blockName === 'money-author-bio');
  const preferred = authorBlocks.find(block => block?.attrs?.data?.type === 'author') ?? authorBlocks[0];
  return (preferred?.attrs?.data as AuthorBioBlockData) ?? null;
};

export const LearnAuthorCard: React.FC<{ post: LearnResponsePage }> = ({ post }) => {
  const { author, authorSlug, verifiedBy, verifiedUserSlug } = post.meta ?? {};
  const bioData = findAuthorBioData(post);
  const name = bioData?.user_name || author;
  if (!name) return null;

  const profileUrl = bioData?.url || (authorSlug ? `${AUTHOR_URL_BASE}${authorSlug}` : null);

  return (
    <div className="order-1 pt-6 lg:order-none">
      <LearnSectionLabel label="About The Author" className="mb-3" />
      <div className={`rounded-[10px] border border-white/10 bg-bzblue-1100 p-4`}>
        <div className="flex items-center gap-3">
          {bioData?.image ? (
            <img alt={name} className={`h-9 w-9 shrink-0 rounded-full object-cover`} src={bioData.image} />
          ) : (
            <span
              aria-hidden="true"
              className={`h-9 w-9 shrink-0 rounded-full inline-flex items-center justify-center bg-bzblue-1000 text-[0.8125rem] font-semibold text-bzblue-400`}
            >
              {initialsOf(name)}
            </span>
          )}
          <div>
            <div className="text-base font-semibold text-white [&_a:hover]:text-bzblue-600">
              {profileUrl ? <a href={profileUrl}>{name}</a> : name}
            </div>
            <div className="mt-0.5 text-xs text-bzblue-500">Contributor, Benzinga</div>
          </div>
        </div>
        {bioData?.bio && (
          <div
            className={`mt-3 rounded-lg bg-bzblue-1000 p-3 text-xs leading-normal text-bzblue-500`}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(bioData.bio) }}
          />
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-3">
          {profileUrl && (
            <a
              className="inline-flex items-center gap-1.5 rounded-md bg-bzblue-1000 px-2.5 py-[5px] text-xs font-semibold text-bzblue-400 hover:bg-white/10 hover:text-white"
              href={profileUrl}
            >
              View Profile
              <Icon icon={faArrowRight} />
            </a>
          )}
          {verifiedBy && (
            <div className="text-xs text-bzblue-500 [&_a:hover]:text-white [&_a]:text-bzblue-400 border px-2 py-1 rounded-md border-[#283D59]">
              Fact checked by{' '}
              {verifiedUserSlug ? <a href={`${AUTHOR_URL_BASE}${verifiedUserSlug}`}>{verifiedBy}</a> : verifiedBy}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearnAuthorCard;
